import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GiphySearch from './GiphySearch';
import PhotoSelector from './PhotoSelector';
import textIcon from '../img/text.png';
import gifIcon from '../img/gif.png';
import photoIcon from '../img/photobooth22.png';
import './MoodboardToolkit.css';

interface MoodboardToolkitProps {
  onAddBlock: (type: 'text' | 'gif' | 'img', dataUrl?: string) => void;
  isLoggedIn: boolean;
  onOpenLoginSignup?: () => void;
  onOpenPhotoBooth?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
}

const MoodboardToolkit: React.FC<MoodboardToolkitProps> = ({ onAddBlock, isLoggedIn, onOpenLoginSignup, onOpenPhotoBooth, isSaving, hasUnsavedChanges }) => {
  const [showGiphySearch, setShowGiphySearch] = useState(false);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [showSaveStatus, setShowSaveStatus] = useState(false);
  const [dots, setDots] = useState('.');
  const { logout, userHandle } = useAuth();

  // Animate the dots for "syncing..."
  useEffect(() => {
    if (hasUnsavedChanges && !isSaving) {
      const interval = setInterval(() => {
        setDots(prev => {
          if (prev === '.') return '..';
          if (prev === '..') return '...';
          return '.';
        });
      }, 500);
      return () => clearInterval(interval);
    }
  }, [hasUnsavedChanges, isSaving]);

  const addTextBlock = () => {
    onAddBlock('text');
  };

  const toggleGiphySearch = () => {
    setShowGiphySearch(!showGiphySearch);
  };

  const handleGifSelect = (gifUrl: string) => {
    onAddBlock('gif', gifUrl);
    setShowGiphySearch(false);
  };

  const togglePhotoSelector = () => {
    setShowPhotoSelector(!showPhotoSelector);
  };

  const handlePhotoSelect = (photoUrl: string) => {
    onAddBlock('img', photoUrl);
    setShowPhotoSelector(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleShare = () => {
    if (userHandle) {
      const shareUrl = `https://mindgarden.page/${userHandle}/inorbit-online_${userHandle}`;
      window.open(shareUrl, '_blank');
    }
  };

  return (
    <>
      <div className="moodboard-toolkit">
        <button
          className="toolkit-button text-button"
          onClick={addTextBlock}
          title="Add Text Block"
        >
          <img src={textIcon} alt="Text" className="toolkit-icon" />
        </button>

        <button
          className="toolkit-button gif-button"
          onClick={toggleGiphySearch}
          title="Add GIF"
        >
          <img src={gifIcon} alt="GIF" className="toolkit-icon" />
        </button>

        <button
          className="toolkit-button photo-button"
          onClick={togglePhotoSelector}
          title="Add Photo from Photo Booth"
        >
          <img src={photoIcon} alt="Photo" className="toolkit-icon" />
        </button>

        {isLoggedIn ? (
          <>
            {hasUnsavedChanges && !isSaving && (
              <span className="syncing-indicator">syncing{dots}</span>
            )}
            {isSaving && <span className="auto-save-indicator">Auto-saved</span>}

            <div className="toolkit-info"></div>

            <button
              className="toolkit-link-button"
              onClick={handleShare}
              title="Share on mindgarden.page"
            >
              link to page
            </button>
          </>
        ) : (
          <button
            className="toolkit-login-text"
            onClick={() => onOpenLoginSignup && onOpenLoginSignup()}
            title="Login to save your work"
          >
            Login/Signup to Save
          </button>
        )}

        {showSaveStatus && (
          <div className="save-status">
            ✅ Saved
          </div>
        )}
      </div>

      {showGiphySearch && (
        <GiphySearch
          onSelectGif={handleGifSelect}
          onClose={() => setShowGiphySearch(false)}
        />
      )}

      {showPhotoSelector && (
        <PhotoSelector
          onSelectPhoto={handlePhotoSelect}
          onClose={() => setShowPhotoSelector(false)}
          onOpenPhotoBooth={onOpenPhotoBooth}
        />
      )}
    </>
  );
};

export default MoodboardToolkit;